/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  ReactNode,
  Suspense, lazy, useEffect,
  useState,
} from 'react';
import {
  Navigate, Outlet, Route, Routes, useLocation, useNavigate,
} from 'react-router-dom';
import 'App.css';
import GlobalInjector from 'Store/GlobalInjector';
import {
  FEED_ROUTE,
  FRESHCHAT_TOKEN, FRESHCHAT_URL, ONBOARDING_ROUTE, RESTRICT_ROUTE, USER_EXPIRED_ROUTE,
} from 'Utils/Constants';
import CustomWidget from 'Pages/customWidget/CustomWidget';
import classNames from 'classnames';
import RestrictUserModal from 'Components/common/modal/RestrictUserModal';
import RestrictLayout from 'Components/layout/RestrictLayout';
import MobileAppBanner from 'Components/common/modal/MobileAppBanner';
import CompanyComparison from 'Pages/companyComparison/CompanyComparison';
import SimilarPeople from 'Pages/person/SimilarPeople';
import { Dropdown } from 'Translation/Dropdown';
import { useSelector } from 'react-redux';
import { selectDisclessPersonality, selectGilroyDiscalimer } from 'Selector/GlobalSearchContentSelector';
import RestrictPage from 'Pages/RestrictPage';
import NotFound from 'Pages/NotFound';
import GlobalSearchRedirections from 'Pages/search/GlobalSearchRedirections';
import Feed from 'Pages/feed/Feed';
import PeoplePage from 'Pages/people/PeoplePage';
import PeopleGroupDetail from 'Pages/people/PeopleGroupDetail';
import Company from 'Pages/company/Company';
import Person from 'Pages/person/Person';
import CompaniesHome from 'Pages/company/CompaniesHome';
import RouteOutletSuspenseFallback from 'Components/common/components/RouteOutletSuspenseFallback';
import SalesIndex from 'Pages/sri/SalesIndex';
import IndustriesHome from 'Pages/industries/IndustriesPage';
import DocumentHub from 'Pages/document/DocumentHub';
import IndustriesDetailPage from 'Pages/industries/IndustriesDetailPage';
import ServiceLinesHome from 'Pages/serviceline/serviceLinesPage';
import ServiceLinesDetailPage from 'Pages/serviceline/serviceLinesDetailPage';
import ArcTypes from 'Pages/arctypes/ArcTypes';
import SearchDetails from 'Pages/search/SearchDetails';
import Settings from 'Pages/person/Settings';
import DeepLinkView from 'deeplinking/DeepLinkView';
import CompanyNotableStaff from 'Pages/company/CompanyNotableStaff';
import Favorites from 'Pages/person/Favorites';
import Login from 'Pages/auth/Login';
import SSO from 'Pages/auth/SSO';
import Onboardingv2 from 'Pages/onboardingRevamp/Onboarding';
import Articles from 'Pages/dailyIq/DailyIq';
import DailyIQCompanies from 'Pages/dailyIq/DailyIQCompanies';
import GuestSearch from 'Pages/search/GuestSearchResults';
import SalesforceRouter from 'Components/Salesforce/SalesforceRouter';
import Layout from './Components/layout/Layout';
import {
  getLocalStorageValue,
  guestRouteCheck,
  isActive,
  isGuest,
  isSalesforce,
  IsMobile,
  navigateToAuthServer,
  skipOnboarding, isEnterpriseUser,
  getLangCookie,
  isGuestfromEmail,
  Options,
  isSalesforceRoute,
} from './Utils/UtilityFunctions';

/** Third-party widget only — keep async so the main app shell does not block on it. */
const Freshchat = lazy(() => import('reactjs-freshchat')
  .then((module) => ({ default: module.Freshchat })));

/** Keeps aside + layout chrome while a lazy child route chunk loads (Suspense at Outlet only). */
const PrivateLayoutWithOutletSuspense = function PrivateLayoutWithOutletSuspense() {
  return (
    <Layout>
      <Suspense fallback={<RouteOutletSuspenseFallback />}>
        <Outlet />
      </Suspense>
    </Layout>
  );
};

const RestrictOutletWithSuspense = function RestrictOutletWithSuspense() {
  return (
    <Suspense fallback={<RouteOutletSuspenseFallback />}>
      <Outlet />
    </Suspense>
  );
};

const PrivateWrapper = function PrivateWrapper() {
  const { pathname, search }: { pathname: string, search: string } = useLocation();
  if (isActive() || isGuestfromEmail()) {
    if (isGuest() || isGuestfromEmail()) {
      if (!guestRouteCheck() && !isGuestfromEmail()) {
        navigateToAuthServer(pathname + search);
      } else {
        return <PrivateLayoutWithOutletSuspense />;
      }
    }
    if (pathname !== ONBOARDING_ROUTE && !skipOnboarding()) return <Navigate to="/onboarding" />;
    const restrictFlag = localStorage.getItem('restrictFlag') === 'true';
    if (pathname.includes('restrict')) {
      return restrictFlag ? <RestrictOutletWithSuspense /> : <Navigate to={FEED_ROUTE} />;
    }
    if (restrictFlag) {
      return <Navigate to={RESTRICT_ROUTE} />;
    }
    return <PrivateLayoutWithOutletSuspense />;
  }
  navigateToAuthServer(pathname + search);
  return null;
};

const getApp = function getApp(app: string, target: string) {
  const root = document.querySelector(target);
  const node = root?.classList?.add(app);
  return node;
};

type Props = {
  children: ReactNode,
};

// interface RedirectIfEndsWithDotProps {
//   children: ReactNode;
// }

// const RedirectIfEndsWithDot: React.FC<RedirectIfEndsWithDotProps> = ({ children }) => {
//   const { pathname, search } = useLocation();
//   const navigate = useNavigate();
//   console.log('here111', location)
//   useEffect(() => {
//     if (pathname.match(/\.$/) || search.match(/\.$/)) {
//       navigate('/notfound', { replace: true });
//     }
//   }, [location, navigate]);

//   return <>{children}</>;
// };

const App = function App(props: Props) {
  const { children } = props;
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState<boolean>(false);
  const { pathname }: { pathname: string } = useLocation();
  const { showDisclaimer } = useSelector(selectGilroyDiscalimer);
  GlobalInjector();
  const disclessPersonality = useSelector(selectDisclessPersonality);

  useEffect(() => {
    window.onbeforeunload = () => {
      window.scrollTo(0, 0);
    };
  }, []);

  useEffect(() => (isSalesforce() || isSalesforceRoute() ? getApp('salesForceApp', '#root') : getApp('salesApp', '#root')), []);
  useEffect(() => (getApp(!isEnterpriseUser() ? 'freeUser' : 'notFreeUser', 'body')), []);
  useEffect(() => {
    document.documentElement.lang = getLangCookie();
  }, [getLangCookie()]);

  useEffect(() => {
    const node = document.querySelector('body');
    if (pathname === ONBOARDING_ROUTE) {
      node?.classList.add('onboarding');
      setShouldRenderDropdown(true);
    } else if (isGuestfromEmail()) {
      setShouldRenderDropdown(true);
      node?.classList.remove('onboarding');
    } else {
      setShouldRenderDropdown(false);
      node?.classList.remove('onboarding');
    }
  }, [pathname]);

  return (
    <div className={classNames('App', { nutenixUser: disclessPersonality })}>
      {!shouldRenderDropdown && isActive() && <Dropdown options={Options} />}
      {isActive() ? (
        <Suspense
          fallback={
            <div />
          }
        >
          <Freshchat
            token={FRESHCHAT_TOKEN}
            host={FRESHCHAT_URL}
            externalId={getLocalStorageValue('email') || ''}
            email={getLocalStorageValue('email') || ''}
            firstName={getLocalStorageValue('fullname') || ''}
            config={{
              headerProperty: {
                hideChatButton: true,
              },
              content: {
                headers: {
                  faq: 'xiQ University',
                },
              },
            }}
            faqTags={{
              tags: ['Sales X'],
              filterType: 'category',
            }}
          />
        </Suspense>
      ) : null}
      {(IsMobile() && !isSalesforce() && !showDisclaimer) ? (
        <MobileAppBanner />
      ) : null}
      {/*
        Public lazy routes (Login, SSO, …) need a root boundary. fallback=null: avoid
        replacing the whole UI with a fake nav during private <Outlet/> loads (fixed above).
      */}
      <Suspense fallback={isSalesforce() ? <div /> : null}>
        <Routes>
          <Route path="sso/:token" element={<SSO />} />
          <Route path="/webapp/" element={<DeepLinkView />} />
          <Route path="/password*" element={<Login />} />
          <Route
            path="article"
            element={(
              <Layout><Articles /></Layout>
            )}
          />
          <Route
            path="guestSearch"
            element={(
              <Layout><GuestSearch /></Layout>)}
          />
          <Route
            path={USER_EXPIRED_ROUTE}
            element={(
              <RestrictLayout>
                <RestrictUserModal />
              </RestrictLayout>
            )}
          />
          {/* All private routes will be nested under this wrapper route */}
          <Route element={<PrivateWrapper />}>
            {/* <Route path="onboarding" element={<Onboarding />} /> */}
            <Route path="onboarding" element={<Onboardingv2 />} />
            <Route path="feed/:section" element={<Feed />} />
            <Route path="feed" element={<Feed />} />
            <Route path="companies" element={<CompaniesHome />} />
            <Route path="company/:section" element={<Company />} />
            <Route path="notablestaff" element={<CompanyNotableStaff />} />
            <Route path="person/:section" element={<Person />} />
            <Route path="settings" element={<Settings />} />
            <Route path="favorites" element={<Favorites />} />
            <Route path="people" element={<PeoplePage />} />
            <Route path="comparecompany" element={<CompanyComparison />} />
            <Route path="industries" element={<IndustriesHome />} />
            <Route path="documents" element={<DocumentHub />} />
            <Route path="industry/:section" element={<IndustriesDetailPage />} />
            <Route path="servicelines" element={<ServiceLinesHome />} />
            <Route path="serviceline/:section" element={<ServiceLinesDetailPage />} />
            <Route path="search" element={<GlobalSearchRedirections />} />
            <Route path="search/globalsearch" element={<SearchDetails />} />
            <Route path="groupdetails/:section" element={<PeopleGroupDetail />} />
            <Route path="arctypes/:section" element={<ArcTypes />} />
            <Route path="sri/:section" element={<SalesIndex />} />
            <Route path="news" element={<CustomWidget />} />
            <Route path="similarpeople" element={<SimilarPeople />} />
            <Route path="salesforce/:section" element={<SalesforceRouter />} />
            {children}
            <Route path="notfound" element={<NotFound />} />
            <Route path="restrict" element={<RestrictPage />} />
            <Route path="dailyiq" element={<DailyIQCompanies />} />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
};
export default App;
